var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// calendar.ts
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import "@adornis/chemistry/elements/components/x-loader.js";
import { html } from "lit";
import { customElement } from "lit/decorators.js";
import { property } from "lit/decorators/property.js";
import { state } from "lit/decorators/state.js";
import { createRef, ref } from "lit/directives/ref.js";
import { until } from "lit/directives/until.js";
var events = [
  "eventClick",
  "eventMouseEnter",
  "eventMouseLeave",
  "eventDragStart",
  "eventDragStop",
  "eventDrop",
  "drop",
  "eventReceive",
  "eventLeave",
  "eventResizeStart",
  "eventResizeStop",
  "eventResize",
  "dateClick",
  "select",
  "unselect"
];
var XCalendar = class extends ChemistryLitElement {
  constructor() {
    super();
    this.events = [];
    this.selectable = false;
    this.editable = false;
    this.day = false;
    this.week = false;
    this.month = false;
    this.loading = false;
    this._container = createRef();
    if (!XCalendar.appliedStyles) {
      XCalendar.appliedStyles = true;
      void import("text:@fullcalendar/common/main.min.css").then(({ default: commonStyle }) => {
        const style = document.createElement("style");
        style.innerHTML = commonStyle;
        document.head.appendChild(style);
      });
    }
  }
  styles() {
    return [
      {
        ":host": {
          position: "relative",
          fontFamily: this.fontFamily,
          "--fc-button-bg-color": this.colors.accent.string,
          "--fc-button-text-color": this.colors.on.accent,
          "--fc-button-border-color": this.colors.accent.shade(500),
          "--fc-button-active-bg-color": this.colors.accent.shade(500),
          "--fc-button-active-border-color": this.colors.accent.shade(500),
          "--fc-button-hover-bg-color": this.colors.accent.shade(500),
          "--fc-button-hover-border-color": this.colors.accent.shade(500)
        },
        ".pointer": { cursor: "pointer" },
        "[loader]": {
          display: "flex",
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          opacity: 0,
          zIndex: 10,
          visibility: "hidden",
          transition: "opacity 0.3s ease-out, visibility 0.3s ease-out",
          pointerEvents: "none"
        },
        "[loader][show]": { opacity: 1, visibility: "visible", pointerEvents: "initial" }
      }
    ];
  }
  get calendarInstance() {
    return this._calendar;
  }
  async firstUpdated() {
    if (!this._container.value)
      return console.error("this cant happen");
    await import("@fullcalendar/core/vdom.js");
    const { Calendar } = await import("@fullcalendar/core");
    const dayGridPlugin = (await import("@fullcalendar/daygrid")).default;
    const interactionPlugin = (await import("@fullcalendar/interaction")).default;
    const listPlugin = (await import("@fullcalendar/list")).default;
    const timeGridPlugin = (await import("@fullcalendar/timegrid")).default;
    const plugins = [];
    let toolbarSwitcher = [];
    if (this.month) {
      plugins.push(dayGridPlugin);
      toolbarSwitcher.push("dayGridMonth");
    }
    if (this.week) {
      plugins.push(timeGridPlugin);
      toolbarSwitcher.push("timeGridWeek");
    }
    if (this.day) {
      plugins.push(listPlugin);
      toolbarSwitcher.push("list");
    }
    if (!plugins.length)
      plugins.push(dayGridPlugin);
    const initialView = toolbarSwitcher[0] ?? "dayGridMonth";
    if (toolbarSwitcher.length < 2)
      toolbarSwitcher = [];
    if (this.selectable)
      plugins.push(interactionPlugin);
    this._calendar = new Calendar(this._container.value, {
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: toolbarSwitcher.join()
      },
      initialView,
      locale: "de",
      buttonText: {
        today: "Heute",
        month: "Monat",
        week: "Woche",
        day: "Tag",
        list: "Tag"
      },
      height: this.style.height ? "100%" : "auto",
      datesSet: ({ start, end }) => {
        if (this.events?.length)
          return console.warn(
            "x-calendar: user changed time range, but events are set using the events property, setting events using the events property is deprecated, use eventsProvider instead to provide events on demand"
          );
        this._subscribeEvents(start, end);
      },
      ...this.options,
      plugins,
      selectable: this.selectable,
      editable: this.editable,
      eventColor: this.colors.accent
    });
    for (const eventName of events) {
      this._calendar.on(eventName, (event) => {
        this.dispatchEvent(new CustomEvent(eventName, { detail: event }));
      });
    }
    this._calendar.render();
    setTimeout(() => {
      this._calendar?.updateSize();
      this.requestUpdate();
    }, 200);
    setTimeout(() => {
      this._calendar?.updateSize();
      this.requestUpdate();
    }, 500);
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    requestAnimationFrame(() => {
      if (changedProperties.get("events")) {
        console.warn(
          "x-calendar: setting events using the 'events' property is deprecated, use 'eventsProvider' instead"
        );
        this._calendar?.removeAllEvents();
        for (const event of this.events)
          this._calendar?.addEvent(event);
      }
    });
  }
  _subscribeEvents(start, end) {
    setTimeout(() => {
      if (this.eventsProvider) {
        this._eventsSubscription?.unsubscribe();
        this._eventsSubscription = this.eventsProvider(start, end).subscribe((events2) => {
          this.loading = true;
          this._calendar?.removeAllEvents();
          for (const event of events2)
            this._calendar?.addEvent(event);
          this.loading = false;
        });
      }
    }, 0);
  }
  render() {
    return html`
      <style>
        ${until(
      //@ts-expect-error css import
      import("text:@fullcalendar/common/main.min.css").then((x) => x.default),
      ""
    )}
          ${until(
      //@ts-expect-error css import
      import("text:@fullcalendar/daygrid/main.min.css").then((x) => x.default),
      ""
    )}
            ${until(
      //@ts-expect-error css import
      import("text:@fullcalendar/list/main.min.css").then((x) => x.default),
      ""
    )}
              ${until(
      //@ts-expect-error css import
      import("text:@fullcalendar/timegrid/main.min.css").then((x) => x.default),
      ""
    )}
      </style>
      <div wrapper ${ref(this._container)}></div>
      <div loader ?show=${this.loading}><x-loader></x-loader></div>
    `;
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this._eventsSubscription?.unsubscribe();
  }
};
XCalendar.appliedStyles = false;
__decorateClass([
  state({ hasChanged: () => true })
], XCalendar.prototype, "events", 2);
__decorateClass([
  property({ type: Boolean })
], XCalendar.prototype, "selectable", 2);
__decorateClass([
  property({ type: Boolean })
], XCalendar.prototype, "editable", 2);
__decorateClass([
  property({ type: Boolean })
], XCalendar.prototype, "day", 2);
__decorateClass([
  property({ type: Boolean })
], XCalendar.prototype, "week", 2);
__decorateClass([
  property({ type: Boolean })
], XCalendar.prototype, "month", 2);
__decorateClass([
  state()
], XCalendar.prototype, "options", 2);
__decorateClass([
  state()
], XCalendar.prototype, "eventsProvider", 2);
__decorateClass([
  state()
], XCalendar.prototype, "loading", 2);
XCalendar = __decorateClass([
  customElement("x-calendar")
], XCalendar);
export {
  XCalendar
};
